import { useCallback, useContext, useState } from "react";
import {
  ContactCardCheckout,
  LoadingOverlay,
} from "@sunrun/experience-ui-components";
import type { Contact } from "@sunrun/experience-ui-components/lib/components/ContactCard";
import { useParams } from "react-router-dom";
import { createSelector } from "@reduxjs/toolkit";
import { Role } from "../../../amplify/backend/function/OfferExpContacts/ts/public/types";
import { useContactManagement } from "hooks/useContactManagement";
import { ROLES } from "utils/usageUtils";
import { ErrorModal } from "components/molecules/ErrorModal";
import { voidAllProposals, regenDocs } from "services/prospect";
import { useAppSelector } from "store";
import { UsageFormContext } from "providers/UsageFormContext";
import { modals } from "fixtures/modals";

const {
  VOID_NEM,
  RESEND_NEM,
  REGEN_CONFIRM,
  REGEN_INFORM,
  SAME_SIGNER,
  SAME_SIGNER_THEN_RESEND_SUNRUN,
  RESEND_SUNRUN_SECONDARY,
  REMOVE_SECONDARY,
} = modals;

type TitleInfoContactsProps = {
  selectedPrimaryContact: Contact | undefined;
  setSelectedPrimaryContact: (contact: Contact) => void;
  selectedSecondaryContact?: Contact | undefined;
  setSelectedSecondaryContact?: (contact: Contact | undefined) => void;
  selectedUtilityContact: Contact | undefined;
  setSelectedUtilityContact: (contact: Contact) => void;
  refSetter?: (node: HTMLDivElement | null) => void;
  hasOffers?: boolean;
  hasSignedOffer?: boolean;
  offerApproved?: boolean;
  refetchContacts: () => void;
};

const TitleInfoContacts = ({
  selectedPrimaryContact,
  setSelectedPrimaryContact,
  selectedSecondaryContact,
  setSelectedSecondaryContact,
  selectedUtilityContact,
  setSelectedUtilityContact,
  refSetter,
  hasOffers,
  hasSignedOffer,
  offerApproved,
  refetchContacts,
}: TitleInfoContactsProps) => {
  const { prospectId = "" } = useParams();
  const { setShowUtilityBillWarningBanner } = useContext(UsageFormContext);

  const [isVoiding, setIsVoiding] = useState(false);
  const [isRegeneratingDocuments, setIsRegeneratingDocuments] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Processing...");

  const selectState = createSelector(
    [
      (state) => state?.auth?.hybridToken,
      (state) => state?.auth?.identity,
      (state) => state?.auth?.cognitoToken?.token,
    ],
    (authKey, identity, cognitoToken) => ({
      authKey: authKey,
      identity: identity,
      cognitoToken: cognitoToken,
    })
  );
  const { authKey, identity, cognitoToken } = useAppSelector(selectState);

  const {
    contactList,
    onAddContact,
    onEditContact,
    onEditContactRole,
    isContactsLoading,
    error,
    setError,
  } = useContactManagement();

  const handleContactUpdate = useCallback(
    async (newContact: Contact, role: Role, currentContact?: Contact) => {
      if (newContact?.contactId !== currentContact?.contactId) {
        // Update ROLE of the contact;
        await onEditContact({
          ...newContact,
          role,
          // Set contact as primary if changing role to Homeowner
          ...(role === ROLES.HOMEOWNER && { primary: true }),
        });
      }
    },
    [onEditContact]
  );

  const handleContactRoleUpdate = useCallback(
    async (newContact: Contact, roleToUpdate: Role, newRole: Role) => {
      try {
        // Update ROLE of the contact
        await onEditContactRole({ ...newContact, roleToUpdate, newRole });
      } catch (error) {
        setError({ action: "Updating Contact", error: error as Error });
      }
    },
    [onEditContactRole, setError]
  );

  const findContactById = (id: string) =>
    contactList?.find((contact) => contact.contactId === id);

  const onContactInfoSave = useCallback(
    async (contactToUpdate: Contact, role?: Role) => {
      const {
        contactId,
        customerFirstName,
        customerLastName,
        customerPrimaryPhone,
        customerEmail,
      } = contactToUpdate;

      const isEditingAnyName = !!contactList?.find((contact) => {
        return (
          contact.contactId === contactId &&
          (contact.customerFirstName !== customerFirstName ||
            contact.customerLastName !== customerLastName)
        );
      });

      if (isEditingAnyName || !contactToUpdate.contactId) {
        let action = "Creating new Contact";
        try {
          setLoadingMessage("Creating contact...");
          setIsLoading(true);
          const savedContact = await onAddContact({
            customerEmail,
            customerFirstName,
            customerLastName,
            customerPrimaryPhone,
            role: role ?? ROLES.OTHER,
            ...(role === ROLES.HOMEOWNER && { primary: true }),
          });

          if (savedContact) {
            if (role === ROLES.HOMEOWNER) {
              setSelectedPrimaryContact(savedContact);
              if (
                hasOffers &&
                savedContact.contactId !== selectedPrimaryContact?.contactId
              ) {
                if (offerApproved) {
                  action = "Voiding Proposals";
                  setLoadingMessage("Voiding offers...");
                  setIsVoiding(true);
                  await voidAllProposals({
                    authKey,
                    prospectId,
                    identity,
                    cognitoToken: cognitoToken ?? "",
                  });
                } else {
                  action = "Regenerating documents";
                  setLoadingMessage("Regenerating documents...");
                  setIsRegeneratingDocuments(true);
                  await regenDocs({
                    authKey,
                    prospectId,
                  });
                }
                await refetchContacts();
              }
            } else if (role === ROLES.CONTRACTCOSIGNER) {
              setSelectedSecondaryContact?.(savedContact);
              await refetchContacts();
            } else if (role === ROLES.UTILITYBILLCONTACT) {
              setSelectedUtilityContact(savedContact);
            }
          }

          return savedContact;
        } catch (error) {
          setError({ action, error: error as Error });
          throw error;
        } finally {
          setIsLoading(false);
          setIsVoiding(false);
          setIsRegeneratingDocuments(false);
          setLoadingMessage("Processing...");
        }
      } else {
        try {
          setIsLoading(true);
          await onEditContact(contactToUpdate);
          setIsLoading(false);
          return contactToUpdate;
        } catch (error) {
          setError({ action: "Editing Contact", error: error as Error });
        }
      }
    },
    [
      authKey,
      cognitoToken,
      contactList,
      hasOffers,
      identity,
      offerApproved,
      onAddContact,
      onEditContact,
      prospectId,
      refetchContacts,
      selectedPrimaryContact?.contactId,
      setError,
      setSelectedPrimaryContact,
      setSelectedSecondaryContact,
      setSelectedUtilityContact,
    ]
  );

  const handlePrimarySelectionUpdate = async (selectedContactId: string) => {
    const selectedContact = findContactById(selectedContactId);

    if (selectedContact) {
      setIsLoading(true);

      await onEditContact({
        ...selectedContact,
        role: ROLES.HOMEOWNER,
        primary: true,
      });
      setSelectedPrimaryContact(selectedContact);
      setIsLoading(false);
      if (
        hasOffers &&
        selectedContactId !== selectedPrimaryContact?.contactId
      ) {
        if (offerApproved) {
          setIsVoiding(true);
          return voidAllProposals({
            authKey,
            prospectId,
            identity,
            cognitoToken: cognitoToken ?? "",
          })
            .catch((error: Error) => {
              setError({ action: "Voiding Proposals", error });
            })
            .finally(async () => {
              await refetchContacts();
              setIsVoiding(false);
            });
        } else {
          setIsRegeneratingDocuments(true);
          return regenDocs({
            authKey,
            prospectId,
          })
            .catch((error: Error) => {
              setError({
                action: "Regenerating documents",
                error,
              });
            })
            .finally(async () => {
              await refetchContacts();
              setIsRegeneratingDocuments(false);
            });
        }
      }
    }
  };

  const handleSecondarySelectionUpdate = async (selectedContactId: string) => {
    const selectedContact = findContactById(selectedContactId);
    setIsLoading(true);
    if (selectedContact) {
      await handleContactUpdate(
        selectedContact,
        ROLES.CONTRACTCOSIGNER,
        selectedSecondaryContact
      )
        .then(async () => {
          setSelectedSecondaryContact?.(selectedContact);
          await refetchContacts();
        })
        .catch((error: Error) => {
          setError({ action: "Updating Secondary Contact", error });
        });
    } else {
      // If selectedContact is undefined, it means the user is trying to remove the secondary signer
      await handleContactRoleUpdate(
        selectedSecondaryContact!,
        ROLES.CONTRACTCOSIGNER,
        ROLES.OTHER
      )
        .then(async () => {
          setSelectedSecondaryContact?.(undefined);
          await refetchContacts();
        })
        .catch((error: Error) => {
          setError({ action: "Removing Secondary Contact", error });
        });
    }
    setIsLoading(false);
  };

  const handleBillingSelectionUpdate = async (selectedContactId: string) => {
    const selectedContact = findContactById(selectedContactId);

    if (selectedContact) {
      setSelectedUtilityContact(selectedContact);
    }
    if (selectedContactId !== selectedUtilityContact?.contactId) {
      setShowUtilityBillWarningBanner(true);
    }
  };

  return (
    <>
      <div ref={refSetter}>
        <ContactCardCheckout
          selectedContactId={selectedPrimaryContact?.contactId}
          toolTipText="The primary signer must be on the title of the home and represent the legal rights surrounding the ownership and use of the residential property."
          contactList={contactList || []}
          contactType="primary"
          cardTitleText="Primary Signer (Optional)"
          showCreditStatus
          disableEditEmail={hasSignedOffer}
          disableEditPhone={hasSignedOffer}
          onContactInfoSave={(updatedContact: Contact) => {
            return onContactInfoSave(updatedContact, ROLES.HOMEOWNER);
          }}
          onSelectionUpdate={handlePrimarySelectionUpdate}
          modals={[VOID_NEM, REGEN_CONFIRM, REGEN_INFORM]}
          hasOffer={hasSignedOffer}
          hasProposals={hasOffers}
          offerApproved={offerApproved}
          disableEditName
          showAgreementSignatureWarning={true}
        />
      </div>

      <div ref={refSetter}>
        <ContactCardCheckout
          selectedContactId={selectedSecondaryContact?.contactId}
          toolTipText="The secondary signer must be on the title of the home and represent the legal rights surrounding the ownership and use of the residential property."
          contactList={contactList || []}
          contactType="secondary"
          cardTitleText="Secondary Signer (Optional)"
          showCreditStatus
          disableEditEmail={hasSignedOffer}
          disableEditPhone={hasSignedOffer}
          onContactInfoSave={(updatedContact: Contact) => {
            return onContactInfoSave(updatedContact, ROLES.CONTRACTCOSIGNER);
          }}
          onSelectionUpdate={(contactId: string) =>
            handleSecondarySelectionUpdate(contactId)
          }
          modals={[
            SAME_SIGNER_THEN_RESEND_SUNRUN,
            SAME_SIGNER,
            RESEND_SUNRUN_SECONDARY,
            REMOVE_SECONDARY,
          ]}
          hasOffer={hasSignedOffer}
          hasProposals={hasOffers}
          offerApproved={offerApproved}
          disableEditName
        />
      </div>
      <div ref={!selectedPrimaryContact ? refSetter : null}>
        <ContactCardCheckout
          disableEditEmail={hasSignedOffer}
          disableEditPhone={hasSignedOffer}
          selectedContactId={selectedUtilityContact?.contactId}
          toolTipText="The utility bill contact must exactly match the name/spelling on the household's utility bills. This contact will be the signer for the NEM agreements and apply for utility approval."
          contactList={contactList || []}
          contactType="utility_bill"
          cardTitleText="Utility Bill Contact (Optional)"
          showCreditStatus={false}
          onContactInfoSave={(updatedContact: Contact) => {
            return onContactInfoSave(updatedContact, ROLES.UTILITYBILLCONTACT);
          }}
          onSelectionUpdate={handleBillingSelectionUpdate}
          modals={[RESEND_NEM]}
          hasOffer={hasOffers}
        />
      </div>

      {!!error && !isRegeneratingDocuments && (
        <ErrorModal
          context="TitleInfoContacts"
          error={error.error}
          action={error.action}
          onClose={() => setError(undefined)}
        />
      )}
      {(!!isVoiding ||
        !!isRegeneratingDocuments ||
        !!isContactsLoading ||
        !!isLoading) && (
        <LoadingOverlay
          message={
            isVoiding
              ? "Voiding offers..."
              : isRegeneratingDocuments
              ? "Regenerating documents..."
              : loadingMessage
          }
          data-testid={
            isVoiding || isRegeneratingDocuments
              ? "loading-overlay"
              : "processing-overlay"
          }
        />
      )}
    </>
  );
};

export { TitleInfoContacts };
export type { TitleInfoContactsProps };
